import { inject, Injectable, signal } from '@angular/core';
import { ApiService } from '../api/api.service';
import { catchError, tap } from 'rxjs';
import {
  AuthenticationStepEnum,
  ICredentials,
  AuthRequestEnum,
  IAuthenticationService,
  AuthenticationResponseCode,
  IUser,
  USER_ROLE_SERVICE_TOKEN,
} from '@aksia/infrastructure';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService implements IAuthenticationService {
  //#region Injections

  //private readonly store = inject(LocalStorageService);
  private readonly api = inject(ApiService);
  private readonly router = inject(Router);
  private readonly userService = inject(USER_ROLE_SERVICE_TOKEN);

  //#endregion

  //#region Properties

  redirectUrl = signal<string | (() => string) | undefined>(undefined);
  statusMessage = signal<string>('');

  authenticationStep = signal<AuthenticationStepEnum>(
    AuthenticationStepEnum.NotAuthenticated,
  );

  //#endregion

  //#region Methods

  signIn(credentials?: ICredentials) {
    this.authenticationStep.set(AuthenticationStepEnum.VerifyingCredentials);
    this.api
      .post(AuthRequestEnum.AUTH, credentials)
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            if (err.status === 401 || err.status === 403) {
              if (
                err.error?.responseCode ===
                AuthenticationResponseCode.NeedVerificationCode
              ) {
                this.statusMessage.set(
                  `Enter the new code you received at: ${err.error.responseDetails}`,
                );
                this.authenticationStep.set(
                  AuthenticationStepEnum.Requested2FA,
                );
              } else {
                this.authenticationStep.set(
                  AuthenticationStepEnum.RejectedAuthentication,
                );
                this.statusMessage.set(
                  'Your account could not be verified. Please try again.',
                );
              }
            }
          }
          throw err.error;
        }),
      )
      .subscribe((response: any): void => {
        if (response?.responseCode === AuthenticationResponseCode.Success) {
          const user: IUser = {
            userName: credentials!.maxusername,
            fullName: response.responseDetails.fullName,
            roles: response.responseDetails?.roles,
          };
          this.onSigningInSuccessfully(user);
        }
      });
  }

  verify2FA(credentials?: ICredentials) {
    this.authenticationStep.set(AuthenticationStepEnum.Verifying2FA);
    this.api
      .post(AuthRequestEnum.AUTH_2FA, credentials)
      .pipe(
        catchError((err) => {
          if (err instanceof HttpErrorResponse) {
            if (err.status === 401 || err.status === 403) {
              if (
                err.error?.responseCode ===
                AuthenticationResponseCode.VerificationCodeIncorrect
              ) {
                this.statusMessage.set(
                  `Your account could not be verified. Please try again.`,
                );
                this.authenticationStep.set(
                  AuthenticationStepEnum.RejectedAuthentication,
                );
              }
            }
          }
          throw err.error;
        }),
      )
      .subscribe((response: any): void => {
        const user: IUser = {
          userName: credentials!.maxusername,
          fullName: response.responseDetails.fullName,
          roles: response.responseDetails?.roles,
        };
        this.onSigningInSuccessfully(user);
      });
  }

  signOut() {
    this.api.post(AuthRequestEnum.SIGN_OUT, {}).subscribe({
      next: (success: any) => {
        this.onSigningOutSuccessfully();
      },
      error: (error: HttpErrorResponse) => {
        console.error(error);
        if (error?.status === 401) {
          this.onSigningOutSuccessfully();
          /* this.authSvc.clearSecurity(); */
        }
      },
    });
  }

  private onSigningInSuccessfully(user: IUser) {
    this.authenticationStep.set(AuthenticationStepEnum.IsAuthenticated);
    this.userService.initActiveUser(user);

    //TODO: This case is currently only for extractor.aksia.com
    if (this.redirectUrl()) {
      let redirectUrl =
        typeof this.redirectUrl() === 'function'
          ? (this.redirectUrl() as Function)()
          : this.redirectUrl();
      document.location.href = redirectUrl;
    }

    let redirectUrl = this.userService.redirectAfterLogin();
    if (redirectUrl) {
      this.userService.redirectAfterLogin.set(undefined);
    } else {
      redirectUrl = '/home';
    }

    this.router.navigateByUrl(redirectUrl);
  }

  private onSigningOutSuccessfully() {
    this.authenticationStep.set(AuthenticationStepEnum.NotAuthenticated);
    this.userService.clearActiveUser();
    this.router.navigateByUrl('/login');
  }

  //#endregion
}
